import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { findLocalDevUser, isLocalDevAuthEnabled, isLocalDevCompanyId, toStoredLocalUser } from '../utils/localDevAuth';
import { clearAuthMessage, clearStoredAuth, consumeAuthMessage, getApiErrorMessage, getProfileCompanyId, hasUsableStoredValue, normalizeAuthProfile, SESSION_EXPIRED_REASON, storeAuthSession } from '../utils/authSession';
import {
  buildSuperadminMfaRequest,
  getSuperadminMfaErrorMessage,
  isSuperadminMfaRequired,
  normalizeSuperadminMfaChallenge,
} from '../utils/superadminMfaFlow';

const SignIn = ({ setUser, setToken, setRefreshToken, setIsAuthenticated, setCompanyId, companyId }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [errorMeta, setErrorMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState([]);
  const [mfaCompletedUser, setMfaCompletedUser] = useState(null);
  const [mfaPendingUser, setMfaPendingUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldShowSessionMessage = searchParams.get('reason') === SESSION_EXPIRED_REASON;

    if (!shouldShowSessionMessage) {
      clearAuthMessage();
      setError('');
      return;
    }

    const authMessage = consumeAuthMessage();
    setError(authMessage || 'Session expired. Please log in again.');
    searchParams.delete('reason');
    navigate({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const loginWithLocalDevCredential = (matchedUser) => {
    const localUser = toStoredLocalUser(matchedUser);
    clearStoredAuth();
    localStorage.setItem('company_id', localUser.company.id);
    localStorage.setItem('user', JSON.stringify(localUser));

    if (setCompanyId) setCompanyId(localUser.company.id);
    setToken(null);
    setRefreshToken(null);
    setUser(localUser);
    setIsAuthenticated(true);
    if (localUser.role === 'admin') navigate('/admin');
    else if (localUser.role === 'manager') navigate('/manager');
    else navigate('/team-member');
  };

  const humanizePaymentError = (code, meta = {}) => {
    const when = meta.expires_at || meta.grace_ends_at || meta.current_period_end;
    const whenTxt = when ? ` (since ${new Date(when).toLocaleString()})` : '';
    switch (code) {
      case 'expired':
        return `Your company's subscription has expired${whenTxt}. Please renew to continue.`;
      case 'no_subscription':
        return `No active subscription found for this company. Please purchase a plan to continue.`;
      case 'cancelled':
        return `This subscription was cancelled${whenTxt}. Please purchase a new plan.`;
      case 'past_due':
        return `Payment is past due${whenTxt}. Please clear dues to restore access.`;
      case 'inactive_company':
        return `Company access is disabled. Contact your administrator.`;
      default:
        return `Access is restricted for this company. Please contact support.`;
    }
  };

  const parseError = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};

    if (err?.message === 'TOKEN_RESPONSE_MISSING') {
      return 'Login token missing from server response. Please try again.';
    }

    const backendMsg = getApiErrorMessage(err, {
      fallback: 'Invalid credentials or server error. Please try again.',
      sessionFallback: 'Invalid email or password.',
    });

    // Subscription / payment issues (your backend uses 402/403 with a code)
    if ((status === 402 || status === 403) && data.code) {
      setErrorCode(data.code);
      setErrorMeta(data);
      return humanizePaymentError(data.code, data);
    }

    if (status === 400) return backendMsg || 'Missing or invalid fields.';
    if (status === 401) return backendMsg || 'Invalid email or password.';
    if (status === 404) return backendMsg || 'Company or profile not found.';
    if (status >= 500) return 'Server error. Please try again later.';
    if (err?.code === 'ECONNABORTED') return 'Login timed out. Please check backend is running and try again.';
    if (!err?.response) return 'Backend server is unavailable. Please try again later.';
    return backendMsg || 'Invalid credentials or server error. Please try again.';
  };

  const parseProfileLoadError = (err) => {
    const status = err?.response?.status;
    const statusLabel = status ? ` (${status})` : '';
    const tokenWasPresent = err?.__putpensTokenPresent ? 'yes' : 'no';

    if (err?.code === 'ECONNABORTED') {
      return 'Login succeeded, but loading your profile timed out. Please try again.';
    }

    if (!err?.response) {
      return 'Login succeeded, but your profile could not be loaded. Please try again.';
    }

    if (status === 401) {
      return `Profile authorization failed${statusLabel}. Token was present: ${tokenWasPresent}.`;
    }

    if (status === 403) {
      return `Login succeeded, but profile access was denied${statusLabel}. Please contact support.`;
    }

    if (status === 404) {
      return `Login succeeded, but your profile was not found${statusLabel}. Please contact support.`;
    }

    if (status >= 500) {
      return `Login succeeded, but the server could not load your profile${statusLabel}. Please try again later.`;
    }

    return `Login succeeded, but your profile could not be loaded${statusLabel}. Please try again.`;
  };

  const clearMfaState = () => {
    setMfaChallenge(null);
    setMfaCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setMfaLoading(false);
    setMfaRecoveryCodes([]);
    setMfaCompletedUser(null);
    setMfaPendingUser(null);
  };

  const completeLoginWithTokens = async ({ access, refresh, effectiveCompanyId, suppressRedirect = false }) => {
    const trimmedAccess = typeof access === 'string' ? access.trim() : '';
    const looksLikeJwt = trimmedAccess.startsWith('eyJ');
    const isAccessValid = (
      typeof access === 'string'
      && trimmedAccess.length > 20
      && looksLikeJwt
    );

    if (!hasUsableStoredValue(trimmedAccess) || !isAccessValid) {
      throw new Error('TOKEN_RESPONSE_MISSING');
    }

    let profileRes;
    try {
      profileRes = await axios.get(`${API_URL}/api/profile/`, {
        headers: {
          Authorization: `Bearer ${trimmedAccess}`,
        },
        __skipAuthRedirect: true,
        timeout: 15000,
      });
    } catch (err) {
      throw Object.assign(err, {
        __putpensLoginStage: 'profile',
        __putpensTokenPresent: Boolean(trimmedAccess),
      });
    }

    const userData = normalizeAuthProfile(profileRes.data, effectiveCompanyId);
    const resolvedCompanyId = getProfileCompanyId(userData, effectiveCompanyId);

    storeAuthSession({
      accessToken: trimmedAccess,
      refreshToken: refresh,
      user: userData,
      companyId: resolvedCompanyId,
    });

    if (resolvedCompanyId && setCompanyId) setCompanyId(resolvedCompanyId);
    setToken(trimmedAccess);
    setRefreshToken(refresh);
    setUser(userData);
    setIsAuthenticated(true);

    if (suppressRedirect) {
      return userData;
    }

    if (userData.role === 'admin') {
      navigate('/admin');
    } else if (userData.role === 'manager') {
      navigate('/manager');
    } else if (userData.role === 'team_member') {
      navigate('/team-member');
    } else {
      clearStoredAuth();
      setUser(null);
      setToken(null);
      setRefreshToken(null);
      setIsAuthenticated(false);
      setError('Invalid role. Please contact support.');
    }

    return userData;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setErrorMeta(null);
    clearMfaState();
    setLoading(true);

    const effectiveCompanyId = companyId || localStorage.getItem('company_id') || '1';
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedPassword = password.trim();
    const shouldUseLocalDevDirectly = isLocalDevAuthEnabled() && isLocalDevCompanyId(effectiveCompanyId);

    clearStoredAuth({ preserveCompanyId: true });
    localStorage.setItem('company_id', String(effectiveCompanyId));
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);

    if (shouldUseLocalDevDirectly) {
      const localUser = findLocalDevUser({
        companyId: effectiveCompanyId,
        email: normalizedEmail,
        password: normalizedPassword,
        role,
      });

      if (localUser) {
        loginWithLocalDevCredential(localUser);
      } else {
        setError('Invalid local development credentials.');
        setLoading(false);
      }
      return;
    }

    try {
      let access;
      let refresh;

      try {
        const { data } = await axios.post(`${API_URL}/api/token/`, {
          email: normalizedEmail,
          password: normalizedPassword,
          role,
          company_id: effectiveCompanyId,
        }, {
          timeout: 15000,
          __skipAuthRedirect: true,
        });

        if (isSuperadminMfaRequired(data)) {
          setMfaChallenge(normalizeSuperadminMfaChallenge(data));
          setMfaPendingUser({
            effectiveCompanyId,
          });
          setLoading(false);
          return;
        }

        access = data?.access || data?.tokens?.access || data?.data?.access;
        refresh = data?.refresh || data?.tokens?.refresh || data?.data?.refresh;
      } catch (err) {
        throw Object.assign(err, { __putpensLoginStage: 'token' });
      }

      try {
        await completeLoginWithTokens({ access, refresh, effectiveCompanyId });
      } catch (err) {
        throw err;
      }
    } catch (err) {
      const canUseLocalFallback = isLocalDevAuthEnabled()
        && (!err?.response || [400, 401, 404].includes(err.response.status));
      const localUser = canUseLocalFallback
        ? findLocalDevUser({ companyId: effectiveCompanyId, email: normalizedEmail, password: normalizedPassword, role })
        : null;

      if (localUser) {
        loginWithLocalDevCredential(localUser);
        return;
      }

      // Clear any stale auth
      clearStoredAuth({ preserveCompanyId: true });
      localStorage.setItem('company_id', String(effectiveCompanyId));
      setUser(null);
      setToken(null);
      setRefreshToken(null);
      setIsAuthenticated(false);

      const msg = err?.__putpensLoginStage === 'profile'
        ? parseProfileLoadError(err)
        : parseError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaChallenge?.challengeId || !mfaPendingUser?.effectiveCompanyId) {
      setError('MFA session expired. Please sign in again.');
      clearMfaState();
      return;
    }

    setError('');
    setErrorCode('');
    setErrorMeta(null);
    setMfaLoading(true);

    try {
      const { path, payload } = buildSuperadminMfaRequest({
        challengeId: mfaChallenge.challengeId,
        code: mfaCode,
        recoveryCode,
        useRecovery,
        enrollmentRequired: mfaChallenge.enrollmentRequired,
      });
      const { data } = await axios.post(`${API_URL}${path}`, payload, {
        timeout: 15000,
        __skipAuthRedirect: true,
      });

      const access = data?.access || data?.tokens?.access || data?.data?.access;
      const refresh = data?.refresh || data?.tokens?.refresh || data?.data?.refresh;
      const recoveryCodes = Array.isArray(data?.recovery_codes) ? data.recovery_codes : [];
      const userData = await completeLoginWithTokens({
        access,
        refresh,
        effectiveCompanyId: mfaPendingUser.effectiveCompanyId,
        suppressRedirect: recoveryCodes.length > 0,
      });

      if (recoveryCodes.length > 0) {
        setMfaChallenge(null);
        setMfaPendingUser(null);
        setMfaCode('');
        setRecoveryCode('');
        setUseRecovery(false);
        setMfaRecoveryCodes(recoveryCodes);
        setMfaCompletedUser(userData);
        return;
      }
      clearMfaState();
    } catch (err) {
      clearStoredAuth({ preserveCompanyId: true });
      setUser(null);
      setToken(null);
      setRefreshToken(null);
      setIsAuthenticated(false);
      const msg = err?.__putpensLoginStage === 'profile'
        ? parseProfileLoadError(err)
        : getSuperadminMfaErrorMessage(err);
      setError(msg);
      if (err?.response?.status === 400 && String(err?.response?.data?.detail || '').toLowerCase().includes('expired')) {
        clearMfaState();
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const PaymentHelp = () => {
    // Show the two buttons only for subscription-related errors
    const isSubError = ['expired', 'no_subscription', 'cancelled', 'past_due', 'inactive_company'].includes(errorCode);
    if (!isSubError) return null;

    return (
      <div className="mt-3 flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="secondary-btn px-3 py-2 text-sm sm:text-base"
          aria-label="Go to landing page"
        >
          Go to Home
        </button>
        <button
          type="button"
          onClick={() => navigate('/enquiry')}
          className="primary-btn px-3 py-2 text-sm sm:text-base"
          aria-label="Renew the your plan"
        >
          Go to Payment
        </button>
      </div>
    );
  };

  const continueAfterRecoveryCodes = () => {
    const userData = mfaCompletedUser;
    clearMfaState();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 overflow-x-hidden">
      <div className="card w-full max-w-md p-5 sm:p-6">
        <h2 className="mb-4 text-center text-xl font-bold tracking-tight text-text sm:mb-6 sm:text-2xl">Sign In</h2>

        {error && (
          <div className="mb-4 sm:mb-5">
            <div className="alert-error">
              {error}
              {errorMeta?.expires_at && (
                <div className="mt-1 text-xs sm:text-sm text-red-700">
                  Expiry: {new Date(errorMeta.expires_at).toLocaleString()}
                </div>
              )}
            </div>
            <PaymentHelp />
          </div>
        )}

        {mfaRecoveryCodes.length > 0 ? (
          <div>
            <div className="mb-4 rounded-lg border border-green-100 bg-green-50 p-3 text-sm text-green-900">
              <p className="font-semibold">Recovery codes generated</p>
              <p className="mt-1">Save these one-time codes in a secure place. They will not be shown again.</p>
            </div>

            <div className="mb-4 grid gap-2">
              {mfaRecoveryCodes.map((code) => (
                <code key={code} className="rounded-md border border-border bg-white px-3 py-2 text-center text-sm text-text">
                  {code}
                </code>
              ))}
            </div>

            <button
              type="button"
              onClick={continueAfterRecoveryCodes}
              className="primary-btn w-full p-2 sm:p-3 text-sm sm:text-base"
            >
              Continue to Dashboard
            </button>
          </div>
        ) : mfaChallenge ? (
          <form onSubmit={handleMfaSubmit}>
            <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
              {mfaChallenge.enrollmentRequired ? (
                <>
                  <p className="font-semibold">Set up Microsoft Authenticator</p>
                  <p className="mt-1">Microsoft Authenticator - Other account - Scan QR code.</p>
                </>
              ) : (
                <>
                  <p className="font-semibold">MFA verification required</p>
                  <p className="mt-1">Enter the current 6-digit code from Microsoft Authenticator.</p>
                </>
              )}
            </div>

            {mfaChallenge.enrollmentRequired && mfaChallenge.qrCode && (
              <div className="mb-4 flex justify-center rounded-lg border border-border bg-white p-4">
                <img
                  src={mfaChallenge.qrCode}
                  alt="Microsoft Authenticator enrollment QR code"
                  className="h-auto w-full max-w-48"
                />
              </div>
            )}

            {!useRecovery ? (
              <div className="mb-3 sm:mb-4">
                <label className="block text-text mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="mfa-code">
                  6-digit code
                </label>
                <input
                  type="text"
                  id="mfa-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                />
              </div>
            ) : (
              <div className="mb-3 sm:mb-4">
                <label className="block text-text mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="recovery-code">
                  Recovery code
                </label>
                <input
                  type="text"
                  id="recovery-code"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                  autoComplete="off"
                  required
                  autoFocus
                />
              </div>
            )}

            {!mfaChallenge.enrollmentRequired && (
              <button
                type="button"
                onClick={() => {
                  setUseRecovery((current) => !current);
                  setMfaCode('');
                  setRecoveryCode('');
                  setError('');
                }}
                className="secondary-btn mb-3 w-full p-2 text-sm sm:text-base"
              >
                {useRecovery ? 'Use Authenticator code' : 'Use recovery code'}
              </button>
            )}

            <button
              type="submit"
              disabled={mfaLoading}
              className="primary-btn w-full p-2 sm:p-3 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed"
              aria-busy={mfaLoading}
            >
              {mfaLoading ? 'Verifying...' : (mfaChallenge.enrollmentRequired ? 'Activate MFA' : 'Verify')}
            </button>

            <button
              type="button"
              onClick={() => {
                clearStoredAuth({ preserveCompanyId: true });
                clearMfaState();
                setError('');
              }}
              className="secondary-btn mt-3 w-full p-2 sm:p-3 text-sm sm:text-base"
            >
              Cancel
            </button>
          </form>
        ) : (
        <form onSubmit={handleLogin}>
          <div className="mb-4 sm:mb-6">
            <label className="block text-text mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="role">
              Role
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
              required
              aria-label="Select role"
            >
              <option value="">Select Role</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="team_member">Team Member</option>
            </select>
          </div>

          <div className="mb-3 sm:mb-4">
            <label className="block text-text mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
              required
              autoComplete="username"
            />
          </div>

          <div className="mb-3 sm:mb-4">
            <label className="block text-text mb-1 sm:mb-2 text-sm sm:text-base" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="primary-btn w-full p-2 sm:p-3 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed"
            aria-busy={loading}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
};

export default SignIn;
